import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Mail, Send } from "lucide-react";
import { normalizeError, support } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useLLM } from "@/contexts/LLMContext";

const TOPICS = [
  { id: "help", label: "Ho bisogno di aiuto" },
  { id: "bug", label: "Segnalo un problema" },
  { id: "feature", label: "Propongo un miglioramento" },
  { id: "privacy", label: "Richiesta sui miei dati" },
  { id: "other", label: "Altro" },
];

export default function SupportDialog({ open, onOpenChange, supportEmail, initialTopic = "help" }) {
  const { showError } = useLLM();
  const { user } = useAuth();
  const [topic, setTopic] = useState(initialTopic);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [diagnostics, setDiagnostics] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await support.send({
        topic,
        subject: subject.trim(),
        message: message.trim(),
        reply_to: replyTo.trim() || undefined,
        include_diagnostics: diagnostics,
      });
      toast[res.sent ? "success" : "info"](res.message);
      setSubject("");
      setMessage("");
      onOpenChange(false);
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="support-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl">Assistenza</DialogTitle>
          <DialogDescription className="numa-meta">
            Scrivici: riceverai risposta all&apos;indirizzo che indichi qui sotto.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium text-ink">Tipo di richiesta</legend>
            <div className="flex flex-wrap gap-1.5">
              {TOPICS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTopic(t.id)}
                  aria-pressed={topic === t.id}
                  className={`rounded-md border px-2.5 py-1.5 text-[12.5px] transition-colors ${
                    topic === t.id
                      ? "border-brand bg-brand-soft font-medium text-brand"
                      : "border-line-strong text-ink-muted hover:bg-surface-2"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="space-y-1.5">
            <Label htmlFor="support-subject">Oggetto</Label>
            <Input
              id="support-subject"
              data-testid="support-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="In una riga, di cosa si tratta"
              className="h-10"
              required
              minLength={3}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-message">Messaggio</Label>
            <Textarea
              id="support-message"
              data-testid="support-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Descrivi la richiesta. Se segnali un problema, indica cosa stavi facendo."
              className="min-h-[130px] resize-none"
              required
              minLength={10}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-reply">Rispondere a</Label>
            <Input
              id="support-reply"
              type="email"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder={user?.email || "il tuo indirizzo email"}
              className="h-10"
            />
            <p className="numa-meta">Lascia vuoto per usare l&apos;email del tuo account.</p>
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-line bg-surface-2 px-3 py-2.5">
            <Checkbox
              checked={diagnostics}
              onCheckedChange={(v) => setDiagnostics(v === true)}
              className="mt-0.5"
            />
            <span className="text-[12.5px] leading-relaxed">
              <span className="font-medium text-ink">Allega informazioni tecniche</span>
              <span className="mt-0.5 block text-ink-muted">
                Browser e provider di modelli attivo. Nessun contenuto dei tuoi documenti.
              </span>
            </span>
          </label>

          <DialogFooter className="items-center justify-between sm:justify-between">
            {supportEmail && (
              <a
                href={`mailto:${supportEmail}`}
                className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-muted hover:text-ink"
              >
                <Mail className="h-3.5 w-3.5" /> {supportEmail}
              </a>
            )}
            <Button type="submit" disabled={sending || !subject.trim() || message.trim().length < 10}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Invia
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
